// src/components/reports/TaskComparisonChart.jsx
import React from 'react';
import { Bar, Line, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TaskComparisonChart = ({ comparisonData, chartType = 'bar' }) => {
  const chartData = {
    labels: [comparisonData.task1.title, comparisonData.task2.title],
    datasets: [{
      label: 'Performance (%)',
      data: [
        comparisonData.task1.mark || comparisonData.task1.averageMark,
        comparisonData.task2.mark || comparisonData.task2.averageMark
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.7)',
        comparisonData.improvement >= 0 ? 'rgba(16, 185, 129, 0.7)' : 'rgba(239, 68, 68, 0.7)'
      ],
      borderColor: [
        'rgb(59, 130, 246)',
        comparisonData.improvement >= 0 ? 'rgb(16, 185, 129)' : 'rgb(239, 68, 68)'
      ],
      borderWidth: 2
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Task Performance Comparison',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.raw.toFixed(1);
            const improvement = context.dataIndex === 1 ? comparisonData.improvement : 0;
            const changeText = improvement !== 0 && context.dataIndex === 1 
              ? ` (${improvement > 0 ? '+' : ''}${improvement.toFixed(1)}% change)`
              : '';
            return `${context.dataset.label}: ${value}%${changeText}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="h-80">
        {chartType === 'bar' && <Bar data={chartData} options={options} />}
        {chartType === 'line' && <Line data={chartData} options={options} />}
      </div>
      
      {/* Performance Summary */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
        <div className="text-center">
          <p className="text-sm text-gray-600">Performance Change</p>
          <p className={`text-lg font-bold ${comparisonData.improvement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {comparisonData.improvement > 0 ? '+' : ''}{comparisonData.improvement.toFixed(1)}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600">Improvement Rate</p>
          <p className={`text-lg font-bold ${comparisonData.improvementPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {comparisonData.improvementPercentage.toFixed(1)}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600">Trend</p>
          <p className={`text-lg font-bold ${comparisonData.improvement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {comparisonData.improvement >= 0 ? '↗ Improving' : '↘ Declining'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TaskComparisonChart;